import {Stack, Typography} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {AppointmentBookingEnum, OdontoBookingTaggingEnum} from 'main/analytics/gaTagsEnum';
import {AlertModal} from 'presentation/components';
import {FlowEnum} from 'presentation/modules/booking/shared/constants/flowEnum';
import {BeBornWellCard} from 'presentation/modules/booking/shared/ui/components/BeBornWellCard';
import {BeBornWellDrawer} from 'presentation/modules/booking/shared/ui/components/BeBornWellDrawer';
import {BeBornWellNotRegister} from 'presentation/modules/booking/shared/ui/components/BeBornWellNotRegister';
import {BookingNotFoundVariantEnum} from 'presentation/modules/booking/shared/ui/components/BookingNotFoundDialog/types';
import {BookingNotFoundListCard} from 'presentation/modules/booking/shared/ui/components/BookingNotFoundListCard';
import {MostSearchedList} from 'presentation/modules/booking/shared/ui/components/MostSearchedList';
import {PendingDialog} from 'presentation/modules/booking/shared/ui/components/PendingDialog';
import {TherapyAlert} from 'presentation/modules/booking/shared/ui/components/TherapyAlert';
import {translations} from 'presentation/translations-new';
import {formatText, isSelectedSpecialty} from 'presentation/utils';
import React, {Fragment} from 'react';
import {HealthDuplicityDialog} from '../HealthDuplicityDialog';
import {ScriptModal} from '../ScriptModal';
import {HealthCard} from './components/HealthCard';
import {useHealthSpecialtiesSelection} from './hooks/useHealthSpecialtiesSelection';
import {CheckBookingModalEnum} from './hooks/useHealthSpecialtiesSelection/types';
import {StyledTypography} from './styles';
import {HealthSpecialtyListProps} from './types';

import {useBeBornWell} from 'presentation/modules/booking/shared/hooks/useBeBornWell';

const {resultFound, serverError, serverErrorTitle, tryAgain, unexpectedError} =
	translations['pt-br'].commons;
const {notFoundModal} = translations['pt-br'].privateFlow.bookingFlow.specialtyFlow;

export function HealthSpecialtiesList({
	handleNextStep,
	specialtiesList,
	mostSearchedSpecialties,
}: HealthSpecialtyListProps) {
	const {
		query,
		handleSelectCard,
		checkBookingModal,
		setCheckBookingModal,
		specialtyCode,
		subSpecialtyCode,
		loadingText,
		resetBookingDataSpecialties,
		handleChooseScriptSuggestion,
		scheduledBooking,
		handleCheckHealthDuplicity,
	} = useHealthSpecialtiesSelection({
		handleNextStep,
	});

	const {
		showBeBornWellCard,
		showBeBornWellNotRegister,
		openBeBornWellDrawer,
		setOpenBeBornWellDrawer,
		handleNotPregnantClick,
		handleShowBeBornWellCard,
		handleShowBeBornWellNotRegister,
		handleContinueToBooking,
		selectedSpecialty,
		handleSelectCardWrapper,
	} = useBeBornWell({
		handleCheckDuplicity: handleCheckHealthDuplicity,
		handleSelectCard,
		setCheckBookingModal,
	});

	return (
		<Fragment>
			{openBeBornWellDrawer ? (
				<BeBornWellDrawer
					open={openBeBornWellDrawer}
					setOpen={setOpenBeBornWellDrawer}
					handleNotPregnant={handleNotPregnantClick}
					handleContinueToBooking={handleContinueToBooking}
					onShowBeBornWellCard={handleShowBeBornWellCard}
					onShowBeBornWellNotRegister={handleShowBeBornWellNotRegister}
				/>
			) : null}

			{showBeBornWellCard ? (
				<BeBornWellCard continueToBooking={handleContinueToBooking} />
			) : null}

			{showBeBornWellNotRegister ? (
				<BeBornWellNotRegister
					selectedSpecialty={selectedSpecialty || undefined}
					handleSelectCard={handleSelectCard}
					checkBookingModal={checkBookingModal}
					handleCheckDuplicity={handleCheckHealthDuplicity}
				/>
			) : null}

			{!showBeBornWellCard && !showBeBornWellNotRegister ? (
				<Fragment>
					{query ? (
						<Typography variant="subtitle2" color="text.secondary">
							{resultFound}
						</Typography>
					) : null}
					<MostSearchedList
						flowType={FlowEnum.HEALTH}
						handleSelectCard={handleSelectCardWrapper}
						mostSearchedArr={mostSearchedSpecialties}
					/>

					{specialtiesList?.map(categoryItem =>
						categoryItem?.list?.length === 0 ? null : (
							<Stack key={categoryItem?.category} mt={2} gap={2}>
								<StyledTypography variant="h6" id={categoryItem?.category}>
									{categoryItem?.category}
								</StyledTypography>
								<Grid container rowSpacing={2} columnSpacing={3}>
									{categoryItem?.list.map((item, index) => (
										<Grid key={index} size={{xs: 12, lg: 6}}>
											<HealthCard
												dataTagId={
													AppointmentBookingEnum.SELECIONOU_ESPECIALIDADE_TEXT
												}
												text={formatText(item?.formattedText)}
												handleClick={() => handleSelectCardWrapper(item)}
												selected={isSelectedSpecialty(
													item,
													specialtyCode,
													subSpecialtyCode,
												)}
												showSpecialtyDetailsButton={item.hasDefinition}
												specialtyCode={item.code}
											/>
										</Grid>
									))}
								</Grid>
							</Stack>
						),
					)}

					<Stack mt={2}>
						<BookingNotFoundListCard
							openModalDataTagId={`${OdontoBookingTaggingEnum.CLICK_NAO_ENCONTREI_ESPECIALIDADE_DESEJADA}${FlowEnum.HEALTH}`}
							modalHeaderModalDataTagId={`${OdontoBookingTaggingEnum.VISUALIZOU_MODAL_NAO_ENCONTREI_ESPECIALIDADE_DESEJADA}${FlowEnum.HEALTH}`}
							modalCloseModalDataTagId={`${OdontoBookingTaggingEnum.CLICK_FECHAR_MODAL_NAO_ENCONTREI_ESPECIALIDADE_DESEJADA}${FlowEnum.HEALTH}`}
							modalFirstActionDataTagId={`${OdontoBookingTaggingEnum.CLICK_CONSULTAR_REDE_ATENDIMENTO}${FlowEnum.HEALTH}`}
							modalSecondActionDataTagId={`${OdontoBookingTaggingEnum.CLICK_FALAR_COM_ATENDIMENTO_ONLINE}${FlowEnum.HEALTH}`}
							flowType={FlowEnum.HEALTH}
							customOrientationText={notFoundModal.cardListText}
							variant={BookingNotFoundVariantEnum.SPECIALTY}
						/>
					</Stack>

					<PendingDialog
						open={
							checkBookingModal === CheckBookingModalEnum.VERIFYING_AUTH ||
							checkBookingModal === CheckBookingModalEnum.VERIFYING_DUPLICITY ||
							checkBookingModal === CheckBookingModalEnum.VERIFYING_DISTRICTS ||
							checkBookingModal === CheckBookingModalEnum.VERIFYING_REQUEST_SCHEDULE
						}
						text={loadingText}
					/>

					{scheduledBooking ? (
						<HealthDuplicityDialog
							open={checkBookingModal === CheckBookingModalEnum.DUPLICITY_STATE}
							setOpen={() => {
								resetBookingDataSpecialties();
								setCheckBookingModal('none');
							}}
							scheduledBooking={scheduledBooking}
						/>
					) : null}
					<ScriptModal
						open={checkBookingModal === CheckBookingModalEnum.SCRIPT_STATE}
						setOpen={() => {
							resetBookingDataSpecialties();
							setCheckBookingModal('none');
						}}
						handleNext={handleChooseScriptSuggestion}
					/>

					<TherapyAlert
						flowType={FlowEnum.HEALTH}
						alertType="needAuth"
						open={checkBookingModal === CheckBookingModalEnum.NEED_AUTH}
						setOpen={() => {
							resetBookingDataSpecialties();
							setCheckBookingModal('none');
						}}
					/>

					<AlertModal
						open={checkBookingModal === CheckBookingModalEnum.ERROR}
						onClose={() => {
							resetBookingDataSpecialties();
							setCheckBookingModal('none');
						}}
						description={serverError}
						title={serverErrorTitle}
						headerTitle={unexpectedError}
						buttonLabel={tryAgain}
						variant="info"
					/>
				</Fragment>
			) : null}
		</Fragment>
	);
}
